import type { ClientErrorReport } from '@cwos/api-client';

/**
 * Client-side error reporting for Admin Web.
 *
 * Before this existed, an exception thrown during render unmounted the entire
 * React tree: the user got a white page, no message, no way back, and nobody
 * ever found out it had happened. The three things that failure needed are all
 * here — a record that it occurred, a way for the UI to say so, and something
 * the user can press.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS AND IS NOT SENT ANYWHERE
 * ---------------------------------------------------------------------------
 *
 * The MESSAGE is not sent. It never leaves the tab. ADR 0011 marks an error
 * message as never safe to transmit, and it is right: in this product a message
 * is whatever the throwing code was holding — a client's name, a coordinate, a
 * note. The user sees it, because it is their own screen and their own data;
 * nothing else does.
 *
 * What IS sent, to Phibian's own API and nowhere else, is identifiers and code
 * locations: a random correlation id, the error's constructor name, the masked
 * route, the bundle release, and file/line/column frames. The reasoning, and
 * why a browser-side redactor over the message was rejected instead, is in
 * `packages/api-client/src/clientErrors.ts`.
 *
 * `installClientErrorReporting()` is what wires the sink up. Until it is called
 * — and in every unit test, which never calls it — nothing is transmitted at
 * all: `setClientErrorSink` still defaults to null, exactly as before.
 *
 * This module stays dependency-free at RUNTIME (the one import above is a type,
 * erased at compile time) so it can be exercised without a DOM, without a
 * bundler and without a network.
 */

export type ClientErrorSource = 'render' | 'window' | 'promise';

/** One stack frame, reduced to a code location. */
export interface StackFrame {
  file: string;
  line: number;
  column: number;
}

export interface ClientError {
  /**
   * Random correlation id, minted here. It is sent as the report's
   * `x-request-id`, so it becomes the API's request id and appears on the
   * server log line; it is also the reference shown to the user. One value,
   * three places — that is the whole point of it.
   */
  id: string;
  source: ClientErrorSource;
  /** A human-readable one-liner. Never assumed to be safe to transmit. */
  message: string;
  /** The constructor name — 'TypeError', 'ApiError'. An identifier, not text. */
  errorType: string;
  /** ISO-8601. */
  at: string;
  /** The client route the failure happened on, with ids masked. */
  route: string;
  /** Code locations from the stack. The stack's message line is never parsed. */
  frames: StackFrame[];
  /** An application code, when the error carried one. */
  errorCode?: string;
  /** The HTTP status, when the failure came from an API call. */
  statusCode?: number;
  /** The `x-request-id` of the API call that failed, when it was one. */
  requestId?: string;
}

/** The most recent reports, newest last. Bounded — a crash loop must not eat memory. */
const MAX_KEPT = 20;
const kept: ClientError[] = [];
const listeners = new Set<(error: ClientError) => void>();
let sink: ((error: ClientError) => void) | null = null;

/** Set by installClientErrorReporting(); 'unknown' until then. */
let release = 'unknown';

// ---------------------------------------------------------------------------
// Turning a thrown value into a report
// ---------------------------------------------------------------------------

/** Characters an identifier field may contain — the API rejects anything else. */
const IDENTIFIER = /^[A-Za-z0-9_.:-]+$/;

/** Reduce a value to an identifier, or undefined if it is not one. */
function asIdentifier(value: unknown, max = 120): string | undefined {
  if (typeof value !== 'string' || value.length === 0 || value.length > max) return undefined;
  return IDENTIFIER.test(value) ? value : undefined;
}

/** Best-effort one-line description of anything a `throw` or a rejection produced. */
export function describeError(err: unknown): string {
  if (err instanceof Error) return err.message || err.name || 'Error';
  if (typeof err === 'string') return err;
  if (err === null || err === undefined) return 'Unknown error';
  if (typeof err === 'object') {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
    try {
      return JSON.stringify(err);
    } catch {
      // Circular or otherwise unserialisable — the type is still worth saying.
      return Object.prototype.toString.call(err);
    }
  }
  return String(err);
}

/**
 * The error's TYPE — its constructor name, which is an identifier chosen by a
 * programmer, not a string built at runtime out of data.
 */
export function errorTypeOf(err: unknown): string {
  if (err instanceof Error) {
    return asIdentifier(err.name) ?? asIdentifier(err.constructor?.name) ?? 'Error';
  }
  if (err === null) return 'Null';
  if (err === undefined) return 'Undefined';
  if (typeof err === 'object') return asIdentifier((err as { name?: unknown }).name) ?? 'Object';
  return asIdentifier(typeof err) ?? 'Unknown';
}

/**
 * V8 (`    at fn (url:12:34)`, `    at url:12:34`) and
 * SpiderMonkey/JavaScriptCore (`fn@url:12:34`, `@url:12:34`).
 */
const V8_FRAME = /^\s*at\s+(?:.*?\s+\()?(\S+):(\d+):(\d+)\)?\s*$/;
const AT_FRAME = /^\s*[^@]*@(\S+):(\d+):(\d+)\s*$/;

const MAX_FRAMES = 12;

/**
 * Pull code locations out of a stack — and NOTHING else.
 *
 * The first line of a V8 stack is `TypeError: <the message>`, so a naive
 * "find something that looks like file:line:col" would happily lift a client's
 * name straight out of a message that happened to end in `:1:2`. Two rules stop
 * that: a line is only considered when it has the exact shape of a frame
 * (`at …` or `…@…`), and the captured file must contain no whitespace. A
 * message line matches neither for any realistic message, and if it somehow
 * matched the first, a message with a space in it fails the second.
 *
 * Query strings and fragments are dropped: a source URL should not be able to
 * carry a parameter along with it.
 */
export function parseStackFrames(stack: unknown, origin?: string): StackFrame[] {
  if (typeof stack !== 'string' || stack.length === 0) return [];
  const frames: StackFrame[] = [];
  for (const line of stack.split('\n')) {
    if (frames.length >= MAX_FRAMES) break;
    const match = V8_FRAME.exec(line) ?? AT_FRAME.exec(line);
    if (!match) continue;
    let file = (match[1] ?? '').split('?')[0]?.split('#')[0] ?? '';
    if (origin && file.startsWith(origin)) file = file.slice(origin.length) || '/';
    if (file.length === 0 || file.length > 300 || /\s/.test(file)) continue;
    const line_ = Number(match[2]);
    const column = Number(match[3]);
    if (!Number.isInteger(line_) || !Number.isInteger(column)) continue;
    frames.push({ file, line: line_, column });
  }
  return frames;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The route a failure happened on, as a SHAPE rather than an instance.
 *
 * Two things are stripped. Ids, because `/documentation/3f2a…` names one
 * client's record and `/documentation/:id` names a screen — and grouping is
 * impossible without it. And the query string and hash entirely, because a
 * search box puts whatever was typed into `?q=`, which in this product is
 * routinely somebody's name.
 *
 * Anything left that is not plain path characters is replaced wholesale rather
 * than escaped: an unexpected segment is far more likely to be data than to be
 * a route worth reading.
 */
export function maskRoute(pathname: string): string {
  const path = (pathname.split('?')[0] ?? '').split('#')[0] ?? '';
  if (!path.startsWith('/')) return '/';
  const masked = path
    .split('/')
    .map((segment) => {
      if (segment === '') return '';
      if (UUID.test(segment)) return ':id';
      if (/^\d+$/.test(segment)) return ':n';
      if (!/^[A-Za-z0-9_.-]+$/.test(segment)) return ':x';
      if (segment.length > 24) return ':id';
      return segment;
    })
    .join('/');
  return masked.length > 200 ? `${masked.slice(0, 197)}...` : masked || '/';
}

/**
 * A release identifier from the running bundle's own URL.
 *
 * Vite emits `/assets/index-<hash>.js`, and that hash IS the build — which is
 * what a file/line/column frame needs to mean anything. Deriving it from
 * `import.meta.url` means no build argument to remember to pass and no way for
 * the value to drift from the code that produced the frames.
 */
export function releaseFromUrl(url: string): string {
  const file = (url.split('?')[0] ?? '').split('/').pop() ?? '';
  const base = file.replace(/\.[a-z]+$/i, '');
  return asIdentifier(base, 120) ?? 'unknown';
}

/** Random enough for a correlation key; it is never a secret or a capability. */
function newId(): string {
  const webcrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof webcrypto?.randomUUID === 'function') {
    try {
      return webcrypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  const rand = () => Math.random().toString(16).slice(2, 10);
  return `c-${Date.now().toString(36)}-${rand()}${rand()}`;
}

function currentPathname(): string {
  const loc = (globalThis as { location?: { pathname?: string } }).location;
  return typeof loc?.pathname === 'string' ? loc.pathname : '/';
}

function currentOrigin(): string | undefined {
  const loc = (globalThis as { location?: { origin?: string } }).location;
  return typeof loc?.origin === 'string' ? loc.origin : undefined;
}

/**
 * The transmittable projection of a report.
 *
 * This function is the boundary: the message exists on `ClientError` and does
 * not exist on what it returns. A unit test asserts exactly that, because the
 * failure it guards against is silent — an added field would ship PHI and
 * nothing would look wrong.
 */
export function toClientErrorReport(error: ClientError): ClientErrorReport {
  return {
    id: error.id,
    source: error.source,
    errorType: error.errorType,
    at: error.at,
    route: error.route,
    release,
    frames: error.frames,
    ...(error.errorCode ? { errorCode: error.errorCode } : {}),
    ...(error.statusCode !== undefined ? { statusCode: error.statusCode } : {}),
    ...(error.requestId ? { requestId: error.requestId } : {}),
  };
}

// ---------------------------------------------------------------------------
// Recording
// ---------------------------------------------------------------------------

/** Attach a transmitting sink (none by default). Returns the previous one. */
export function setClientErrorSink(
  next: ((error: ClientError) => void) | null,
): ((error: ClientError) => void) | null {
  const previous = sink;
  sink = next;
  return previous;
}

export function reportClientError(source: ClientErrorSource, err: unknown): ClientError {
  const detail = (typeof err === 'object' && err !== null ? err : {}) as {
    stack?: unknown;
    code?: unknown;
    status?: unknown;
    statusCode?: unknown;
    requestId?: unknown;
  };
  const status = typeof detail.status === 'number' ? detail.status : detail.statusCode;

  const error: ClientError = {
    id: newId(),
    source,
    message: describeError(err),
    errorType: errorTypeOf(err),
    at: new Date().toISOString(),
    route: maskRoute(currentPathname()),
    frames: parseStackFrames(detail.stack, currentOrigin()),
    ...(asIdentifier(detail.code) ? { errorCode: asIdentifier(detail.code) } : {}),
    ...(typeof status === 'number' && status >= 100 && status <= 599 ? { statusCode: status } : {}),
    ...(asIdentifier(detail.requestId) ? { requestId: asIdentifier(detail.requestId) } : {}),
  };

  kept.push(error);
  if (kept.length > MAX_KEPT) kept.shift();
  // A listener or sink that throws must not become a second error while we are
  // handling the first one — that is how a crash loop starts.
  for (const listener of [...listeners]) {
    try {
      listener(error);
    } catch {
      // ignored on purpose
    }
  }
  if (sink) {
    try {
      sink(error);
    } catch {
      // ignored on purpose
    }
  }
  return error;
}

export function subscribeClientErrors(listener: (error: ClientError) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function recentClientErrors(): readonly ClientError[] {
  return kept;
}

export function clearClientErrors(): void {
  kept.length = 0;
}

// ---------------------------------------------------------------------------
// Transmission
// ---------------------------------------------------------------------------

/**
 * How many distinct crash sites one page load may report.
 *
 * A render loop can call reportClientError hundreds of times a second. The
 * dedupe below already collapses a loop to one report, but a page that is
 * failing in many places at once still must not become a flood — the endpoint
 * is on the rate limiter's default tier and would start returning 429s, which
 * is a worse failure than losing the eleventh distinct crash.
 */
const MAX_TRANSMITTED = 10;

export interface ClientErrorReportingOptions {
  /** The bundle URL to derive a release from — pass `import.meta.url`. */
  moduleUrl?: string;
  /** Sends one report. Injected so this module keeps no runtime dependency. */
  transmit: (report: ClientErrorReport) => Promise<unknown>;
}

/**
 * Wire the sink up to the API. Call once, at startup.
 *
 * Everything here is defensive on purpose, because this code runs at the exact
 * moment the application is already broken:
 *
 *   - A report is fire-and-forget and its rejection is swallowed. A failed
 *     report must never surface as an unhandled rejection, because
 *     installGlobalErrorHandlers would catch that and report it, which would
 *     fail, which would... that loop is the reason this is written this way.
 *   - The same crash site is reported ONCE per page load. `errorType` plus the
 *     top frame is the identity; the server computes the real grouping digest.
 *   - A hard cap ends transmission for the page after MAX_TRANSMITTED distinct
 *     sites.
 *
 * Returns a function that detaches the sink again.
 */
export function installClientErrorReporting(options: ClientErrorReportingOptions): () => void {
  if (options.moduleUrl) release = releaseFromUrl(options.moduleUrl);
  const seen = new Set<string>();
  let sent = 0;
  let sending = false;

  const transmitting = (error: ClientError) => {
    // Re-entry guard, and deliberately only a SYNCHRONOUS one: if `transmit`
    // throws on the way out and that throw is itself reported, this returns
    // instead of recursing. It must not span the request, or a page failing in
    // several places within one tick would report only the first of them.
    if (sending) return;
    if (sent >= MAX_TRANSMITTED) return;
    const top = error.frames[0];
    const site = `${error.errorType}@${top ? `${top.file}:${top.line}:${top.column}` : error.route}`;
    if (seen.has(site)) return;
    seen.add(site);
    sent += 1;

    sending = true;
    try {
      void options.transmit(toClientErrorReport(error)).catch(() => {
        // The API is unreachable or refused the report. The record is still in
        // this tab and still on the user's screen; there is nothing further this
        // code could usefully do, and retrying would be the loop above.
      });
    } catch {
      // `transmit` threw before returning a promise — the app is broken enough
      // that fetch itself is unavailable. Nothing more to do.
    } finally {
      sending = false;
    }
  };

  setClientErrorSink(transmitting);
  return () => {
    if (sink === transmitting) setClientErrorSink(null);
  };
}

// ---------------------------------------------------------------------------
// Global handlers
// ---------------------------------------------------------------------------

/** The slice of `window` this module uses — narrow so it can be tested without a DOM. */
export interface ErrorEventTargetLike {
  addEventListener: (type: string, listener: (event: unknown) => void) => void;
  removeEventListener: (type: string, listener: (event: unknown) => void) => void;
}

/**
 * Catch what escapes React: an exception on a timer or in an event handler
 * (`error`), and a promise nobody handled (`unhandledrejection`) — the shape a
 * failed fetch usually takes.
 *
 * `addEventListener`, not `window.onerror =`. Assigning the handler property
 * replaces whatever else set it (a dev tool, a future one of these), and the
 * last writer silently wins.
 *
 * Idempotent, and returns a function that removes the handlers again.
 */
export function installGlobalErrorHandlers(target: ErrorEventTargetLike): () => void {
  const onError = (event: unknown) => {
    const detail = (event as { error?: unknown; message?: unknown } | null) ?? {};
    reportClientError('window', detail.error ?? detail.message ?? event);
  };
  const onRejection = (event: unknown) => {
    const detail = (event as { reason?: unknown } | null) ?? {};
    reportClientError('promise', detail.reason ?? event);
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}
